<script setup lang="ts">
/**
 * Selling-prices editor for SMS entries, grouped by currency.
 *
 * Sell: `{ [sms_type_id]: { steps?, packages? } }`
 * Buy:  `{ [aggregator_id]: { [sms_type_id]: { steps?, packages? } } }`
 *
 * UI: DataGrid of directions (country / operator / direction) with
 * master-detail tabs «Продажа» / «Закупка».
 */
import { computed, onMounted, ref, watch } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxSelectBox } from 'devextreme-vue/select-box';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import {
  DxDataGrid,
  DxColumn,
  DxLookup,
  DxEditing,
  DxMasterDetail,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { showError } from '@botto/core';
import { getCountriesLookup, getOperatorsLookup } from '../../lookups';
import { buyTemplatesAdapter } from '../../api/adapters/buy-templates';
import { getExchangeRate } from '../../api/services/currencies';
import { convertSmsPriceUiEntries } from '../../api/price-conversion';
import {
  buyTemplateSmsFromPrices,
  deepCloneAggregatorSmsTypeMap,
  isAggregatorSmsTypeMapEmpty,
  isLegacySmsPrices,
  toAggregatorSmsTypeMap,
  toSmsTypeBillingMap,
} from '../../api/normalizers';
import { fetchCurrencyOptions } from '../../api';
import type {
  ByCurrency,
  SmsPriceEntry,
  AggregatorSmsTypeMap,
  SmsTypeBillingMap,
} from '../../api/types';
import TariffCurrencyTabs from '../../components/TariffCurrencyTabs.vue';
import SmsTypeBillingEditor from './SmsTypeBillingEditor.vue';
import SmsTypeOnlyBillingEditor from './SmsTypeOnlyBillingEditor.vue';

interface UiEntry {
  __key: string;
  country_iso: string;
  operator_id: number | null;
  direction_id: string;
  sell_prices: SmsTypeBillingMap;
  buy_prices: AggregatorSmsTypeMap;
}

const props = defineProps<{
  modelValue: ByCurrency<SmsPriceEntry> | undefined;
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: ByCurrency<SmsPriceEntry>): void;
}>();

const currencies = ref<string[]>([]);
const currentCurrency = ref('');
const entriesByCurrency = ref<Record<string, UiEntry[]>>({});
const availableCurrencyOptions = ref<{ value: string; text: string }[]>([]);
const addingCurrency = ref(false);

const buyTemplateOptions = ref<{ id: number; name: string }[]>([]);
const selectedTemplateByEntry = ref<Record<string, number | null>>({});
/** Preserve Продажа/Закупка tab across model sync (pushUpdate remounts detail otherwise). */
const detailTabIndexByKey = ref<Record<string, number>>({});

const countriesLookup = getCountriesLookup();
const operatorsLookup = getOperatorsLookup();

const lookupsReady = computed(
  () => countriesLookup.loaded.value && operatorsLookup.loaded.value,
);

const countryItems = computed(() => [...countriesLookup.items.value]);
const operatorItems = computed(() => [...operatorsLookup.items.value]);

function countryDisplayExpr(item: { country_iso?: string; country?: string | null } | null): string {
  if (!item) return '';
  return countriesLookup.options.format?.(item as any) ?? item.country ?? item.country_iso ?? '';
}

function operatorDisplayExpr(item: { id?: number; operator?: string | null } | null): string {
  if (!item) return '';
  return operatorsLookup.options.format?.(item as any) ?? item.operator ?? `#${item.id}`;
}

let rowKeySeq = 0;
function newRowKey(): string {
  rowKeySeq += 1;
  return `sms-dir-${Date.now().toString(36)}-${rowKeySeq}`;
}

function sellPricesFromEntry(entry: SmsPriceEntry): SmsTypeBillingMap {
  if (isLegacySmsPrices(entry.prices)) return {};
  return toSmsTypeBillingMap(entry.prices);
}

function indexByKey(rowKey: string): number {
  const list = entriesByCurrency.value[currentCurrency.value] ?? [];
  return list.findIndex((e) => e.__key === rowKey);
}

/** Match stored ISO to lookup key (billing may differ by case). */
function resolveCountryIso(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  const items = countriesLookup.items.value;
  const exact = items.find((c) => c.country_iso === raw);
  if (exact) return exact.country_iso;
  const lower = raw.toLowerCase();
  const ci = items.find((c) => (c.country_iso ?? '').toLowerCase() === lower);
  return ci?.country_iso ?? raw;
}

// Start loading lookups during setup so the grid is not painted with empty editors.
void Promise.all([countriesLookup.ensureLoaded(), operatorsLookup.ensureLoaded()]);

onMounted(async () => {
  await Promise.all([countriesLookup.ensureLoaded(), operatorsLookup.ensureLoaded()]);
  // Align stored ISO casing with lookup keys once items are available.
  for (const list of Object.values(entriesByCurrency.value)) {
    for (const row of list) {
      const resolved = resolveCountryIso(row.country_iso);
      if (resolved != null && resolved !== row.country_iso) row.country_iso = resolved;
    }
  }
  try {
    const [templatesRes, currencyCodes] = await Promise.all([
      buyTemplatesAdapter.list({
        offset: 0,
        limit: 500,
        sortName: 'name',
        sortDirection: 'asc',
      }),
      fetchCurrencyOptions(),
    ]);
    buyTemplateOptions.value = templatesRes.items.map((t) => ({ id: t.id, name: t.name }));
    availableCurrencyOptions.value = currencyCodes;
  } catch (e) {
    showError(e);
  }
});

watch(
  () => props.modelValue,
  (v) => {
    const next: Record<string, UiEntry[]> = {};
    const fromApi = v ?? {};
    const presentCurrencies = [
      ...new Set([...Object.keys(fromApi), ...currencies.value]),
    ].sort((a, b) => a.localeCompare(b));
    for (const cur of presentCurrencies) {
      if (cur in fromApi) {
        const prevList = entriesByCurrency.value[cur] ?? [];
        next[cur] = (fromApi[cur] ?? []).map<UiEntry>((entry, i) => {
          const country_iso = entry.country_iso ?? '';
          const operator_id =
            entry.operator_id && entry.operator_id > 0 ? entry.operator_id : null;
          const direction_id = entry.direction_id ?? '';
          const sell_prices = sellPricesFromEntry(entry);
          const buy_prices = toAggregatorSmsTypeMap(entry.buy_prices);
          const prev = prevList[i];
          // Mutate in place so DataGrid master-detail / TabPanel are not remounted
          // when only nested buy/sell prices change (e.g. selecting SMS types).
          if (prev) {
            prev.country_iso = country_iso;
            prev.operator_id = operator_id;
            prev.direction_id = direction_id;
            prev.sell_prices = sell_prices;
            prev.buy_prices = buy_prices;
            return prev;
          }
          return {
            __key: newRowKey(),
            country_iso,
            operator_id,
            direction_id,
            sell_prices,
            buy_prices,
          };
        });
      } else {
        next[cur] = entriesByCurrency.value[cur] ?? [];
      }
    }
    currencies.value = presentCurrencies;
    entriesByCurrency.value = next;
    if (!currentCurrency.value || !presentCurrencies.includes(currentCurrency.value)) {
      currentCurrency.value = presentCurrencies[0] ?? '';
    }
  },
  { immediate: true, deep: true },
);

const currentEntries = computed(() =>
  currentCurrency.value ? (entriesByCurrency.value[currentCurrency.value] ?? []) : [],
);

function pushUpdate(): void {
  const out: ByCurrency<SmsPriceEntry> = {};
  for (const cur of currencies.value) {
    const list = entriesByCurrency.value[cur] ?? [];
    if (list.length === 0) {
      out[cur] = [];
      continue;
    }
    out[cur] = list.map<SmsPriceEntry>((ui) => ({
      country_iso: ui.country_iso,
      operator_id: ui.operator_id && ui.operator_id > 0 ? ui.operator_id : null,
      direction_id: ui.direction_id || null,
      prices: toSmsTypeBillingMap(ui.sell_prices),
      buy_prices: isAggregatorSmsTypeMapEmpty(ui.buy_prices)
        ? undefined
        : toAggregatorSmsTypeMap(ui.buy_prices),
    }));
  }
  emit('update:modelValue', out);
}

async function onAddCurrency(payload: { currency: string; copyFrom?: string }): Promise<void> {
  if (currencies.value.includes(payload.currency)) return;
  addingCurrency.value = true;
  try {
    let entries: UiEntry[] = [];
    if (payload.copyFrom) {
      const source = entriesByCurrency.value[payload.copyFrom] ?? [];
      const rate = await getExchangeRate(payload.copyFrom, payload.currency);
      entries = convertSmsPriceUiEntries(source, rate).map((e) => ({
        ...e,
        __key: newRowKey(),
      }));
    }
    entriesByCurrency.value[payload.currency] = entries;
    currencies.value = [...currencies.value, payload.currency].sort((a, b) =>
      a.localeCompare(b),
    );
    currentCurrency.value = payload.currency;
    pushUpdate();
  } catch (e) {
    showError(e);
  } finally {
    addingCurrency.value = false;
  }
}

function addEntry(): void {
  if (!currentCurrency.value) return;
  const list = entriesByCurrency.value[currentCurrency.value] ?? [];
  list.push({
    __key: newRowKey(),
    country_iso: '',
    operator_id: null,
    direction_id: '',
    sell_prices: {},
    buy_prices: {},
  });
  entriesByCurrency.value[currentCurrency.value] = list;
  pushUpdate();
}

function removeEntryByKey(rowKey: string): void {
  const list = entriesByCurrency.value[currentCurrency.value] ?? [];
  const idx = list.findIndex((e) => e.__key === rowKey);
  if (idx < 0) return;
  list.splice(idx, 1);
  delete selectedTemplateByEntry.value[rowKey];
  delete detailTabIndexByKey.value[rowKey];
  pushUpdate();
}

function detailTabIndex(rowKey: string): number {
  return detailTabIndexByKey.value[rowKey] ?? 0;
}

function setDetailTabIndex(rowKey: string, index: number | null | undefined): void {
  if (typeof index !== 'number' || index < 0) return;
  detailTabIndexByKey.value[rowKey] = index;
}

function onGridRowUpdated(): void {
  const list = entriesByCurrency.value[currentCurrency.value] ?? [];
  for (const row of list) {
    const resolved = resolveCountryIso(row.country_iso);
    if (resolved != null) row.country_iso = resolved;
    if (row.operator_id != null && row.operator_id <= 0) row.operator_id = null;
  }
  pushUpdate();
}

async function applyBuyTemplate(rowKey: string): Promise<void> {
  const templateId = selectedTemplateByEntry.value[rowKey];
  if (!templateId) return;
  const idx = indexByKey(rowKey);
  if (idx < 0) return;
  const list = entriesByCurrency.value[currentCurrency.value];
  if (!list?.[idx]) return;
  try {
    const tpl = await buyTemplatesAdapter.byKey?.(templateId);
    if (!tpl) return;
    list[idx].buy_prices = deepCloneAggregatorSmsTypeMap(
      buyTemplateSmsFromPrices(tpl.prices),
    );
    pushUpdate();
  } catch (e) {
    showError(e);
  }
}

function onSellPricesUpdate(rowKey: string, next: SmsTypeBillingMap): void {
  const idx = indexByKey(rowKey);
  if (idx < 0) return;
  const list = entriesByCurrency.value[currentCurrency.value];
  if (!list?.[idx]) return;
  list[idx].sell_prices = next;
  pushUpdate();
}

function onBuyPricesUpdate(rowKey: string, next: AggregatorSmsTypeMap): void {
  const idx = indexByKey(rowKey);
  if (idx < 0) return;
  const list = entriesByCurrency.value[currentCurrency.value];
  if (!list?.[idx]) return;
  list[idx].buy_prices = next;
  pushUpdate();
}
</script>

<template>
  <div class="sms-prices-editor">
    <div class="spe-toolbar">
      <TariffCurrencyTabs
        :currencies="currencies"
        :current-currency="currentCurrency"
        :available-currency-options="availableCurrencyOptions"
        :adding="addingCurrency"
        @update:current-currency="currentCurrency = $event"
        @add="onAddCurrency"
      />
      <DxButton
        icon="add"
        text="Добавить направление"
        type="default"
        styling-mode="contained"
        :disabled="!currentCurrency"
        @click="addEntry"
      />
    </div>

    <div v-if="!currentCurrency" class="spe-empty">
      Добавьте валюту, чтобы настроить цены SMS.
    </div>

    <div v-else-if="!lookupsReady" class="spe-empty">
      Загрузка справочников…
    </div>

    <div v-else-if="currentEntries.length === 0" class="spe-empty">
      Цены SMS для {{ currentCurrency }} пока не заданы.
    </div>

    <DxDataGrid
      v-else
      class="spe-grid"
      :data-source="currentEntries"
      key-expr="__key"
      :show-borders="true"
      :show-row-lines="true"
      :column-auto-width="true"
      :hover-state-enabled="true"
      :paging="{ enabled: false }"
      @row-updated="onGridRowUpdated"
    >
      <DxScrolling mode="standard" />
      <DxEditing mode="cell" :allow-updating="true" :allow-deleting="false" :allow-adding="false" />

      <DxColumn data-field="country_iso" caption="Страна" :min-width="220">
        <DxLookup
          :data-source="countryItems"
          value-expr="country_iso"
          :display-expr="countryDisplayExpr"
          :allow-clearing="true"
        />
      </DxColumn>
      <DxColumn data-field="operator_id" caption="Оператор" :min-width="220">
        <DxLookup
          :data-source="operatorItems"
          value-expr="id"
          :display-expr="operatorDisplayExpr"
          :allow-clearing="true"
        />
      </DxColumn>
      <DxColumn data-field="direction_id" caption="MCC | MNC" :min-width="160" />

      <DxColumn type="buttons" :width="60" caption="">
        <DxGridButton
          hint="Удалить"
          icon="trash"
          :on-click="(e: any) => removeEntryByKey(e.row.data.__key)"
        />
      </DxColumn>

      <DxMasterDetail :enabled="true" template="detail" />

      <template #detail="{ data: row }">
        <div class="spe-detail">
          <DxTabPanel
            :selected-index="detailTabIndex(row.data.__key)"
            :animation-enabled="true"
            :swipe-enabled="false"
            :defer-rendering="false"
            height="auto"
            @update:selected-index="(i: number) => setDetailTabIndex(row.data.__key, i)"
          >
            <DxTabItem title="Продажа">
              <div class="spe-tab-body">
                <SmsTypeOnlyBillingEditor
                  :model-value="row.data.sell_prices"
                  @update:model-value="(v) => onSellPricesUpdate(row.data.__key, v)"
                />
              </div>
            </DxTabItem>
            <DxTabItem title="Закупка">
              <div class="spe-tab-body">
                <div class="spe-template-row">
                  <DxSelectBox
                    :value="selectedTemplateByEntry[row.data.__key] ?? null"
                    :items="buyTemplateOptions"
                    display-expr="name"
                    value-expr="id"
                    placeholder="Шаблон закупки"
                    :search-enabled="true"
                    width="320"
                    @value-changed="
                      (e: any) => (selectedTemplateByEntry[row.data.__key] = e.value ?? null)
                    "
                  />
                  <DxButton
                    text="Скопировать из шаблона"
                    type="default"
                    styling-mode="contained"
                    :disabled="!selectedTemplateByEntry[row.data.__key]"
                    @click="applyBuyTemplate(row.data.__key)"
                  />
                </div>
                <SmsTypeBillingEditor
                  :model-value="row.data.buy_prices"
                  @update:model-value="(v) => onBuyPricesUpdate(row.data.__key, v)"
                />
              </div>
            </DxTabItem>
          </DxTabPanel>
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<style scoped>
.sms-prices-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
}
.spe-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.spe-empty {
  color: #9ca3af;
  padding: 24px;
  text-align: center;
  border: 1px dashed #e5e7eb;
  border-radius: 4px;
}
.spe-grid {
  width: 100%;
}
.spe-detail {
  padding: 8px 4px 12px;
}
.spe-tab-body {
  padding: 12px 4px 4px;
}
.spe-template-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
</style>

<style>
/* Master-detail cell: avoid double scrollbars / opaque fill. */
.spe-grid .dx-datagrid-rowsview .dx-master-detail-row:not(.dx-datagrid-edit-form) > .dx-datagrid-group-space,
.spe-grid .dx-datagrid-rowsview .dx-master-detail-row:not(.dx-datagrid-edit-form) .dx-master-detail-cell {
  background-color: transparent;
}
</style>
